// `common/services/typeaheads.service.ng1.ts` — the criteria builder's autocomplete sources.
//
// ⚠️ THREE OF THE FIVE ARE OUTSIDE THE EXTENSION REALM, and that is why every tag field on
// both builders opened an empty list. `/extension-skill/search`, `/specialty/search` and
// `/geo/*` are plain careero paths; until they were added to `PROXY_PREFIXES` the SPA's own
// router matched them and answered 200 with its index.html. A 200 full of HTML is the worst
// shape this bug could take: no error to catch, no empty array to notice, just a list that
// never opens.
//
// ⚠️ AND EVERY ONE OF THEM ANSWERS A DIFFERENT SHAPE. Skills and industries are arrays of
// STRINGS, the titles are objects with a `text`, and the two geo calls return full location
// records. `Tag.source` carries the record so the save can send it back — see `criteriaSave`.
import { request } from './request.ts'

/** The shape every one of these is normalised to: what `TagsInput` renders. */
export interface TypeaheadTag {
  text: string
  /**
   * `addNew` — ⚠️ A SUGGESTION, NOT A FLAG ON AN EXISTING ONE. Upstream appends
   * `{ text: query, addNew: true }` when the query itself is not already in the list, so the
   * user can take their own words from the list on a field that otherwise refuses free text.
   * Dropping it makes `add-from-autocomplete-only` fields unable to accept a new skill at all.
   */
  addNew?: boolean
  inDemand?: boolean
  source?: Readonly<Record<string, unknown>>
}

/** `arrayIncludes(..., text)` — the case-sensitive match upstream uses before appending. */
function withAddNew(tags: TypeaheadTag[], query: string, allow: boolean): TypeaheadTag[] {
  if (!allow || query === '') return tags
  if (tags.some((tag) => tag.text === query)) return tags
  return [...tags, { text: query, addNew: true }]
}

/**
 * `textLengthAndAddNewResultSorter` — shortest text first, and the add-new row LAST however
 * short it is.
 *
 * ⚠️ IT IS A `result-sorter` ATTRIBUTE ON THE AUTOCOMPLETE, so the server's order is not what
 * the user sees: measured against build 354 for "java", prod lists Java, Java8, JavaFX, Java 8
 * where the response order gave Java, JavaFX, JavaScript, Java8. Two chained comparators,
 * both stable, which is why ties keep the response's order.
 */
export function sortTypeaheadResults(tags: readonly TypeaheadTag[]): TypeaheadTag[] {
  const byLength = [...tags].sort((a, b) => a.text.length - b.text.length)
  return byLength.sort((a, b) => Number(a.addNew === true) - Number(b.addNew === true))
}

/** `/extension-skill/search` — an array of plain strings. */
export async function getExtensionSkills(query: string, allowAddNew = true): Promise<TypeaheadTag[]> {
  const found = await request<string[]>(
    'GET',
    `/extension-skill/search?${new URLSearchParams({ q: query }).toString()}`,
  )
  return sortTypeaheadResults(withAddNew((found ?? []).map((text) => ({ text })), query, allowAddNew))
}

/** `/specialty/search` — also plain strings, despite the name. */
export async function getExtensionIndustries(query: string, allowOther = true): Promise<TypeaheadTag[]> {
  const found = await request<string[]>(
    'GET',
    `/specialty/search?${new URLSearchParams({ q: query }).toString()}`,
  )
  return sortTypeaheadResults(withAddNew((found ?? []).map((text) => ({ text })), query, allowOther))
}

/** `ITitleQueryResponse` — the title endpoints answer objects, not strings. */
interface TitleResponse {
  text: string
  hasTitleClass?: boolean
  titleClass?: boolean
  titlesAmount?: number
  isDev?: boolean
}

/**
 * `getJobTitles` (`search.page.component.ng1.ts:2011`) — and which endpoint it is depends on
 * a feature flag, not on the field.
 *
 * ⚠️ `excludeTitles` IS OMITTED WHEN EMPTY, NOT SENT BLANK. Upstream passes the array to
 * `$http`, which drops an empty one; the titles already chosen in the row are excluded from
 * the suggestions so the same title cannot be added twice.
 */
export async function getJobTitles(
  query: string,
  excludeTitles: readonly string[] = [],
  extendedTitleClassEnabled = true,
  allowAddNew = true,
): Promise<TypeaheadTag[]> {
  const params = new URLSearchParams({ q: query })
  for (const title of excludeTitles) params.append('excludeTitles', title)
  const path = extendedTitleClassEnabled
    ? '/chrome-extension-api/extension-title-entity/searchWithTitleClasses'
    : '/chrome-extension-api/extension-floating-title/search'
  const found = await request<TitleResponse[] | string[]>('GET', `${path}?${params.toString()}`)
  const tags = (found ?? []).map((entry) =>
    typeof entry === 'string' ? { text: entry } : { text: entry.text, source: { ...entry } },
  )
  return withAddNew(tags, query, allowAddNew)
}

/** `IExtensionLocation` as the geo endpoints answer it. */
interface GeoLocation {
  id?: number
  city?: string
  region_state?: string
  country?: string
  lat?: number
  lon?: number
  lng?: number
  inDemand?: boolean
  normalizedRegion?: string
}

/** The label a geo record shows — the city, else the region. */
function geoText(entry: GeoLocation): string {
  return entry.city ?? entry.region_state ?? entry.normalizedRegion ?? ''
}

/**
 * `getCities` — ⚠️ AND IT RETURNS NOTHING WITHOUT A COUNTRY unless `allowNullCountry`.
 * Upstream short-circuits on `!searchQuery || (!country && !allowNullCountry)`, which is why
 * the distance-from field works before a country is chosen and the Israel-only city field
 * does not.
 */
export async function getCities(
  query: string,
  country: string | undefined,
  options: { populateInDemand?: boolean; allowNullCountry?: boolean; allowOther?: boolean } = {},
): Promise<TypeaheadTag[]> {
  if (query === '' || (country === undefined && options.allowNullCountry !== true)) return []
  const params = new URLSearchParams({ q: query })
  if (country !== undefined) params.set('country', country)
  params.set('populateInDemand', String(options.populateInDemand === true))
  const found = await request<GeoLocation[]>('GET', `/geo/searchCities?${params.toString()}`)
  const tags = (found ?? []).map((entry) => ({
    text: geoText(entry),
    ...(entry.inDemand === true ? { inDemand: true } : {}),
    source: { ...entry },
  }))
  return withAddNew(tags, query, options.allowOther === true)
}

/** `getLocationsOrRegionsFull` — cities AND regions, for the global field. */
export async function getLocationsOrRegionsFull(
  query: string,
  country: string | undefined,
  allowOther = false,
): Promise<TypeaheadTag[]> {
  if (query === '') return []
  const params = new URLSearchParams({ q: query })
  if (country !== undefined) params.set('country', country)
  const found = await request<GeoLocation[]>('GET', `/geo/searchWithRegions?${params.toString()}`)
  const tags = (found ?? []).map((entry) => ({ text: geoText(entry), source: { ...entry } }))
  return withAddNew(tags, query, allowOther)
}

/**
 * `/chrome-extension-api/client-contact-alert/getHighestActivityLevelsAutocompleteValues`.
 *
 * ⚠️ SORTED, LIKE ITS SIBLINGS. The field declares
 * `result-sorter="ctrl.textLengthAndAddNewResultSorter"`
 * (`client-contact-search.page.tpl.html:61`), and the server's own order is not it: measured
 * against the branch tenant, the endpoint answers placements / hiring / open-jobs in pairs
 * while production lists "Had open jobs, Had placements, Had hiring processes, Their company
 * had …" — which is exactly shortest-first.
 */
export async function getHighestActivityLevelTags(query: string): Promise<TypeaheadTag[]> {
  const found = await request<Array<{ text?: string; value?: string }>>(
    'GET',
    `/chrome-extension-api/client-contact-alert/getHighestActivityLevelsAutocompleteValues?${new URLSearchParams({ searchQuery: query }).toString()}`,
  )
  return sortTypeaheadResults(
    (found ?? []).map((entry) => ({ text: entry.text ?? entry.value ?? '', source: { ...entry } })),
  )
}

/**
 * `getCompanySizes` / `getCompanyTypes` (`typeaheads.service.ng1.ts:356`) — the two
 * `jobCompanyInfoEnabled` company fields, both `add-from-autocomplete-only`.
 *
 * ⚠️ `company-search`, NOT `client-contact`. The sibling employer autocomplete on the same
 * service lives under `/chrome-extension-api/client-contact/employerAutoComplete` and takes
 * `query`; these two take `q` under a different controller entirely.
 */
export async function getCompanySizes(query: string): Promise<TypeaheadTag[]> {
  const found = await request<Array<string | { text?: string }>>(
    'GET',
    `/chrome-extension-api/company-search/searchCompanySizes?${new URLSearchParams({ q: query }).toString()}`,
  )
  return (found ?? []).map((entry) => (typeof entry === 'string' ? { text: entry } : { text: entry.text ?? '' }))
}

export async function getCompanyTypes(query: string): Promise<TypeaheadTag[]> {
  const found = await request<Array<string | { text?: string }>>(
    'GET',
    `/chrome-extension-api/company-search/searchCompanyTypes?${new URLSearchParams({ q: query }).toString()}`,
  )
  // ⚠️ SORTED, unlike sizes. The size list is a set of ranges whose order is meaningful
  // ("1 - 10 employees" before "11 - 50"); upstream leaves it alone and applies
  // `textLengthAndAddNewResultSorter` to types only.
  return sortTypeaheadResults((found ?? []).map((entry) => (typeof entry === 'string' ? { text: entry } : { text: entry.text ?? '' })))
}

/**
 * `getRootTitleClasses` (`typeaheads.service.ng1.ts:195`) — "Categories hiring for", the third
 * control inside the contact form's high-hiring-likelihood block.
 *
 * ⚠️ `extension-title-entity`, NOT `title-class`. The obvious-looking
 * `/chrome-extension-api/title-class/searchRootTitleClasses` is a 404 that answers with a
 * Tomcat error PAGE, not JSON — so a client that only checks the status sees HTML where it
 * expects an array.
 */
export async function getRootTitleClasses(query: string): Promise<TypeaheadTag[]> {
  const found = await request<Array<string | { text?: string }>>(
    'GET',
    `/chrome-extension-api/extension-title-entity/searchRootTitleClasses?${new URLSearchParams({ q: query }).toString()}`,
  )
  // ⚠️ NOT SORTED, AND SORTING IT CHANGED WHICH CATEGORY A USER PICKS. The industries field
  // beside it carries `result-sorter="ctrl.textLengthAndAddNewResultSorter"`; this one carries
  // no sorter at all, so the server's own order stands. Measured against build 354 driving the
  // same query: production's first suggestion for "d" was "Administration" and ours was
  // "Design" — the saved criterion differed on a field neither side had typed differently.
  return (found ?? []).map((e) => (typeof e === 'string' ? { text: e } : { text: e.text ?? '' }))
}

/** `TitleRecommendation` — the Suggestions block's rows. */
export interface TitleRecommendation {
  title: string
  /** Splits the block into "Extended Categories" (true) and "Specific Titles" (false). */
  titleClass: boolean
  topTitlesInClass: string[]
  titlesAmount: number
  highlyRelevant: boolean
}

/**
 * `getTitleRecommendationsForTitlesV2` — the titles suggested from the ones already chosen.
 *
 * ⚠️ THE BODY IS A BARE ARRAY OF STRINGS, not an object wrapping one. Upstream sends
 * `data: titles`, so `{ titles }` is a different request.
 */
export async function getTitleRecommendations(titles: readonly string[]): Promise<TitleRecommendation[]> {
  return request('POST', '/chrome-extension-api/title-recommendations/getTitleRecommendationsForTitlesV2', [
    ...titles,
  ])
}
